let controlChar: BluetoothRemoteGATTCharacteristic | null = null;
let indoorBikeDataChar: BluetoothRemoteGATTCharacteristic | null = null;

let powerChar: BluetoothRemoteGATTCharacteristic | null = null;
let powerCallback: ((_watts: number) => void) | null = null;

let hrChar: BluetoothRemoteGATTCharacteristic | null = null;
let hrCallback: ((_bpm: number) => void) | null = null;

let cadenceChar: BluetoothRemoteGATTCharacteristic | null = null;
let cadenceCallback: ((_rpm: number) => void) | null = null;

// Cadence source configuration
export type CadenceSource = 'trainer' | 'external' | 'disabled';
let cadenceSource: CadenceSource = 'disabled';

// Store the last crank revolution data for cadence calculation
let lastCrankRevolutions: number | null = null;
let lastCrankEventTime: number | null = null;

export function onPowerUpdate(callback: (_watts: number) => void) {
  powerCallback = callback;

  // Return cleanup function
  return () => {
    powerCallback = null;
  };
}

export function onHeartRateUpdate(callback: (_bpm: number) => void) {
  hrCallback = callback;

  // Return cleanup function
  return () => {
    hrCallback = null;
  };
}

export function onCadenceUpdate(callback: (_rpm: number) => void) {
  cadenceCallback = callback;

  // Return cleanup function
  return () => {
    cadenceCallback = null;
  };
}

export function clearPowerCallback() {
  powerCallback = null;
}

export function clearHeartRateCallback() {
  hrCallback = null;
}

export function clearCadenceCallback() {
  cadenceCallback = null;
}

export function setCadenceSource(source: CadenceSource) {
  cadenceSource = source;
}

export function getCadenceSource(): CadenceSource {
  return cadenceSource;
}

export async function connectToTrainer() {
  if (!navigator.bluetooth)
    throw new Error(
      'Web Bluetooth not supported. Use Chrome on desktop or Android.'
    );

  const device = await navigator.bluetooth.requestDevice({
    filters: [{ services: ['fitness_machine'] }],
    optionalServices: ['cycling_power', 'cycling_speed_and_cadence'],
  });

  const server = await device.gatt!.connect();

  // Connect to Fitness Machine Service
  const ftms = await server.getPrimaryService('fitness_machine');
  controlChar = await ftms.getCharacteristic('fitness_machine_control_point');

  // Try to get Indoor Bike Data characteristic for cadence from trainer
  try {
    indoorBikeDataChar = await ftms.getCharacteristic('indoor_bike_data');
    await indoorBikeDataChar.startNotifications();

    indoorBikeDataChar.addEventListener(
      'characteristicvaluechanged',
      (event) => {
        const value = (event.target as BluetoothRemoteGATTCharacteristic)
          .value!;

        // Parse FTMS Indoor Bike Data
        const flags = value.getUint16(0, /*littleEndian=*/ true);
        let offset = 2;

        // Skip instantaneous speed if present (bit 0)
        if (flags & 0x01) {
          offset += 2;
        }

        // Skip average speed if present (bit 1)
        if (flags & 0x02) {
          offset += 2;
        }

        // Check if instantaneous cadence is present (bit 2)
        if (flags & 0x04) {
          const cadenceRaw = value.getUint16(offset, /*littleEndian=*/ true);
          const cadence = cadenceRaw * 0.5; // Unit is 0.5 rpm
          if (cadenceSource === 'trainer' && cadenceCallback) {
            cadenceCallback(Math.round(cadence));
          }
          offset += 2;
        }
      }
    );
  } catch (e) {
    // Trainer does not support Indoor Bike Data characteristic
    console.error(e);
  }

  // Connect to Cycling Power Service (for power data and possibly cadence)
  try {
    const cps = await server.getPrimaryService('cycling_power');
    powerChar = await cps.getCharacteristic('cycling_power_measurement');
    await powerChar.startNotifications();

    powerChar.addEventListener('characteristicvaluechanged', (event) => {
      const value = (event.target as BluetoothRemoteGATTCharacteristic).value!;
      const watts = value.getUint16(2, /*littleEndian=*/ true);
      if (powerCallback) powerCallback(watts);

      // Check if we should extract cadence from trainer power data
      // Only use this if FTMS doesn't provide cadence
      if (
        cadenceSource === 'trainer' &&
        !indoorBikeDataChar &&
        cadenceCallback
      ) {
        const cadence = parseCadenceFromPowerMeter(value);
        if (cadence > 0) {
          cadenceCallback(cadence);
        }
      }
    });
  } catch (e) {
    // Trainer does not support Cycling Power Service
    console.error(e);
  }

  // Try Cycling Speed and Cadence service as last resort for trainers
  if (cadenceSource === 'trainer' && !indoorBikeDataChar) {
    try {
      const csc = await server.getPrimaryService('cycling_speed_and_cadence');
      const cscMeasurement = await csc.getCharacteristic('csc_measurement');
      await cscMeasurement.startNotifications();

      cscMeasurement.addEventListener('characteristicvaluechanged', (event) => {
        const value = (event.target as BluetoothRemoteGATTCharacteristic)
          .value!;
        if (cadenceCallback) {
          const cadence = parseCadenceFromCSC(value);
          if (cadence > 0) {
            cadenceCallback(cadence);
          }
        }
      });
    } catch (e) {
      // Trainer does not support Cycling Speed and Cadence Service
      console.error(e);
    }
  }

  return controlChar;
}

export async function connectToHeartRateMonitor() {
  if (!navigator.bluetooth)
    throw new Error(
      'Web Bluetooth not supported. Use Chrome on desktop or Android.'
    );

  const device = await navigator.bluetooth.requestDevice({
    filters: [{ services: ['heart_rate'] }],
  });

  const server = await device.gatt!.connect();
  const hrs = await server.getPrimaryService('heart_rate');
  hrChar = await hrs.getCharacteristic('heart_rate_measurement');
  await hrChar.startNotifications();

  hrChar.addEventListener('characteristicvaluechanged', (event) => {
    const value = (event.target as BluetoothRemoteGATTCharacteristic).value!;

    // Parse heart rate measurement according to Bluetooth spec
    const flags = value.getUint8(0);
    const is16Bit = flags & 0x01;

    let heartRate: number;
    if (is16Bit) {
      heartRate = value.getUint16(1, /*littleEndian=*/ true);
    } else {
      heartRate = value.getUint8(1);
    }

    if (hrCallback) hrCallback(heartRate);
  });

  return hrChar;
}

export async function connectToCadenceSensor() {
  if (!navigator.bluetooth)
    throw new Error(
      'Web Bluetooth not supported. Use Chrome on desktop or Android.'
    );

  const device = await navigator.bluetooth.requestDevice({
    filters: [
      { services: ['cycling_power'] }, // Power meters like Favero Assioma
      { services: ['cycling_speed_and_cadence'] }, // Dedicated cadence sensors
      { name: 'ASSIOMA' }, // Specific support for Assioma
      { namePrefix: 'ASSIOMA' },
    ],
    optionalServices: [
      'cycling_power',
      'cycling_speed_and_cadence',
      'device_information',
    ],
  });

  const server = await device.gatt!.connect();

  // First try cycling power service (for power meters)
  try {
    const cps = await server.getPrimaryService('cycling_power');
    cadenceChar = await cps.getCharacteristic('cycling_power_measurement');
    await cadenceChar.startNotifications();

    cadenceChar.addEventListener('characteristicvaluechanged', (event) => {
      const value = (event.target as BluetoothRemoteGATTCharacteristic).value!;

      if (cadenceCallback) {
        const cadence = parseCadenceFromPowerMeter(value);
        if (cadence > 0) {
          cadenceCallback(cadence);
        }
      }
    });

    return cadenceChar;
  } catch (e) {
    // Device does not support Cycling Power Service, trying CSC
    console.error(e);
  }

  // Try cycling speed and cadence service
  try {
    const csc = await server.getPrimaryService('cycling_speed_and_cadence');
    cadenceChar = await csc.getCharacteristic('csc_measurement');
    await cadenceChar.startNotifications();

    cadenceChar.addEventListener('characteristicvaluechanged', (event) => {
      const value = (event.target as BluetoothRemoteGATTCharacteristic).value!;

      if (cadenceCallback) {
        const cadence = parseCadenceFromCSC(value);
        if (cadence > 0) {
          cadenceCallback(cadence);
        }
      }
    });

    return cadenceChar;
  } catch (e2) {
    console.error(e2);
    throw new Error(
      'No cadence data available from this device. Make sure your device supports cadence measurement.'
    );
  }
}

// Parse cadence from power meter data (Favero Assioma and similar)
function parseCadenceFromPowerMeter(value: DataView): number {
  try {
    const flags = value.getUint16(0, /*littleEndian=*/ true);

    // Check if crank revolution data is present (bit 5 = 0x20)
    const hasCrankRevolutionData = (flags & 0x20) !== 0;

    if (!hasCrankRevolutionData) {
      return 0;
    }

    // Calculate offset to crank revolution data according to Bluetooth spec
    let offset = 2; // Flags (16 bits)
    offset += 2; // Instantaneous Power (16 bits) - always present

    // Pedal Power Balance Present (bit 0)
    if (flags & 0x01) {
      offset += 1;
    }

    // Pedal Power Balance Reference (bit 1) - no additional data

    // Accumulated Torque Present (bit 2)
    if (flags & 0x04) {
      offset += 2;
    }

    // Accumulated Torque Source (bit 3) - no additional data

    // Wheel Revolution Data Present (bit 4)
    if (flags & 0x10) {
      offset += 6; // 4 bytes cumulative wheel revolutions + 2 bytes last wheel event time
    }

    // Crank Revolution Data Present (bit 5) - we know this is true
    if (value.byteLength >= offset + 4) {
      const crankRevolutions = value.getUint16(offset, /*littleEndian=*/ true);
      const crankEventTime = value.getUint16(
        offset + 2,
        /*littleEndian=*/ true
      );

      return calculateCadenceFromRevolutions(crankRevolutions, crankEventTime);
    }
  } catch (e) {
    console.warn('Error parsing cadence from power meter:', e);
  }

  return 0;
}

// Parse cadence from Cycling Speed and Cadence service
function parseCadenceFromCSC(value: DataView): number {
  try {
    const flags = value.getUint8(0);
    const hasCadenceData = (flags & 0x02) !== 0;

    if (!hasCadenceData) {
      return 0;
    }

    let offset = 1; // Skip flags byte

    // Skip wheel revolution data if present (bit 0)
    if (flags & 0x01) {
      offset += 6; // 4 bytes cumulative wheel revolutions + 2 bytes last wheel event time
    }

    // Read crank revolution data (bit 1)
    if (value.byteLength >= offset + 4) {
      const crankRevolutions = value.getUint16(offset, /*littleEndian=*/ true);
      const crankEventTime = value.getUint16(
        offset + 2,
        /*littleEndian=*/ true
      );

      return calculateCadenceFromRevolutions(crankRevolutions, crankEventTime);
    }
  } catch (e) {
    console.warn('Error parsing cadence from CSC:', e);
  }

  return 0;
}

// Calculate cadence RPM from crank revolution data
function calculateCadenceFromRevolutions(
  crankRevolutions: number,
  crankEventTime: number
): number {
  // First data point - just store it
  if (lastCrankRevolutions === null || lastCrankEventTime === null) {
    lastCrankRevolutions = crankRevolutions;
    lastCrankEventTime = crankEventTime;
    return 0;
  }

  // Calculate differences handling 16-bit rollover
  let revDiff = crankRevolutions - lastCrankRevolutions;
  if (revDiff < 0) {
    revDiff += 65536; // Handle 16-bit unsigned rollover
  }

  let timeDiff = crankEventTime - lastCrankEventTime;
  if (timeDiff < 0) {
    timeDiff += 65536; // Handle 16-bit unsigned rollover
  }

  // Only calculate if we have meaningful data
  if (revDiff > 0 && timeDiff > 0 && timeDiff < 12288) {
    // ~12 seconds max
    // Time is in 1/1024 seconds, convert to minutes
    const timeInMinutes = timeDiff / 1024 / 60;
    const cadence = Math.round(revDiff / timeInMinutes);

    // Sanity check for realistic cadence values
    if (cadence >= 20 && cadence <= 200) {
      lastCrankRevolutions = crankRevolutions;
      lastCrankEventTime = crankEventTime;
      return cadence;
    }
  }

  // If calculation failed but we have new data, still update the stored values
  if (timeDiff > 0) {
    lastCrankRevolutions = crankRevolutions;
    lastCrankEventTime = crankEventTime;
  }

  return 0;
}

export function isHeartRateConnected(): boolean {
  return hrChar !== null;
}

export function isCadenceConnected(): boolean {
  return cadenceChar !== null || indoorBikeDataChar !== null;
}

export async function getTrainerControlChar() {
  if (!controlChar) throw new Error('Trainer not connected');
  return controlChar;
}

export async function sendErgCommand(
  char: BluetoothRemoteGATTCharacteristic,
  watts: number
) {
  const buffer = new Uint8Array([0x05, watts & 0xff, (watts >> 8) & 0xff]);
  await char.writeValue(buffer);
}
